import { supabase, getCurrentUserId } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import {
  DRAWER_BASELINE_MIN_RECEIPTS,
  estimateTokensFromChars,
  TOKEN_SAVINGS_SURFACES,
  tokensSaved,
  type TokenSavingsLever,
  type TokenSavingsSurface,
} from "@shared/tokenSavings";

export const tokenSavingsQueryKey = (userId: string | undefined, range: string) =>
  ["token-savings", userId, range] as const;

export type TokenSavingsRange = "7d" | "30d" | "all";

export type TokenSavingsEventInput = {
  userId?: string | null;
  surface: TokenSavingsSurface;
  lever: TokenSavingsLever;
  baselineTokens?: number | null;
  actualTokens?: number | null;
  callsAvoided?: number;
  model?: string | null;
  threadId?: string | null;
  metadata?: Record<string, unknown>;
};

export type TokenSavingsSurfaceRow = {
  surface: TokenSavingsSurface;
  tokensSaved: number;
  baselineTokens: number;
  actualTokens: number;
  tokensUsed: number;
  callsAvoided: number;
  eventCount: number;
  percent: number | null;
  levers: Array<{ lever: TokenSavingsLever; tokensSaved: number; eventCount: number }>;
};

export type TokenSavingsSummary = {
  range: TokenSavingsRange;
  totalSaved: number;
  totalUsed: number;
  callsAvoided: number;
  chatSaved: number;
  mcpSaved: number;
  restSaved: number;
  bySurface: TokenSavingsSurfaceRow[];
};

function rangeStart(range: TokenSavingsRange): Date | null {
  if (range === "all") return null;
  const days = range === "7d" ? 7 : 30;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}

function toInt(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

export const tokenSavingsService = {
  async averageAiRoomTokens(userId: string): Promise<number | null> {
    const { data, error } = await supabase
      .from("operating_loop_receipts")
      .select("total_tokens")
      .eq("user_id", userId)
      .eq("path", "ai_room")
      .not("total_tokens", "is", null)
      .gt("total_tokens", 0)
      .order("created_at", { ascending: false })
      .limit(20);
    if (error || !data?.length) return null;
    const values = data
      .map((row: { total_tokens: number | null }) => toInt(row.total_tokens))
      .filter((n) => n > 0);
    if (values.length < DRAWER_BASELINE_MIN_RECEIPTS) return null;
    return Math.round(values.reduce((sum, n) => sum + n, 0) / values.length);
  },

  async recordEvent(input: TokenSavingsEventInput): Promise<void> {
    try {
      const userId = input.userId ?? (await getCurrentUserId());
      if (!userId) return;
      const baseline = input.baselineTokens ?? null;
      const actual = input.actualTokens ?? 0;
      const saved = tokensSaved(baseline, actual);
      const callsAvoided = input.callsAvoided ?? 0;
      if (saved <= 0 && callsAvoided <= 0) return;
      const { error } = await supabase.from("token_savings_events").insert({
        user_id: userId,
        surface: input.surface,
        lever: input.lever,
        baseline_tokens: baseline,
        actual_tokens: actual,
        tokens_saved: saved,
        calls_avoided: callsAvoided,
        model: input.model ?? null,
        thread_id: input.threadId ?? null,
        metadata: input.metadata ?? {},
      });
      if (error) {
        console.warn("[token-savings] insert failed:", error.message);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["token-savings", userId] });
    } catch (error) {
      console.warn("[token-savings] record failed:", error);
    }
  },

  recordPackFromChars(input: {
    userId?: string | null;
    surface: TokenSavingsSurface;
    lever: TokenSavingsLever;
    baselineChars: number;
    actualChars: number;
    model?: string | null;
    threadId?: string | null;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    const baselineTokens = estimateTokensFromChars(input.baselineChars);
    const actualTokens = estimateTokensFromChars(input.actualChars);
    return this.recordEvent({
      userId: input.userId,
      surface: input.surface,
      lever: input.lever,
      baselineTokens,
      actualTokens,
      model: input.model,
      threadId: input.threadId,
      metadata: { ...input.metadata, baselineChars: input.baselineChars, actualChars: input.actualChars },
    });
  },

  async getSummary(userId: string, range: TokenSavingsRange = "all"): Promise<TokenSavingsSummary> {
    const from = rangeStart(range);
    const fromIso = from?.toISOString() ?? null;

    let eventsQuery = supabase
      .from("token_savings_events")
      .select("surface, lever, tokens_saved, baseline_tokens, actual_tokens, calls_avoided")
      .eq("user_id", userId);
    if (fromIso) eventsQuery = eventsQuery.gte("created_at", fromIso);

    let usageQuery = supabase
      .from("ai_usage_logs")
      .select("app, total_tokens")
      .eq("user_id", userId);
    if (fromIso) usageQuery = usageQuery.gte("created_at", fromIso);

    const [eventsRes, usageRes] = await Promise.all([eventsQuery, usageQuery]);
    if (eventsRes.error) console.warn("[token-savings] summary events failed:", eventsRes.error.message);
    if (usageRes.error) console.warn("[token-savings] summary usage failed:", usageRes.error.message);

    const usedBySurface = new Map<string, number>();
    for (const row of usageRes.data ?? []) {
      const app = typeof row.app === "string" && row.app.trim() ? row.app.trim() : "other";
      usedBySurface.set(app, (usedBySurface.get(app) ?? 0) + toInt(row.total_tokens));
    }

    type Agg = {
      tokensSaved: number;
      baselineTokens: number;
      actualTokens: number;
      callsAvoided: number;
      eventCount: number;
      levers: Map<TokenSavingsLever, { tokensSaved: number; eventCount: number }>;
    };
    const bySurface = new Map<TokenSavingsSurface, Agg>();
    const ensure = (surface: TokenSavingsSurface): Agg => {
      let row = bySurface.get(surface);
      if (!row) {
        row = {
          tokensSaved: 0,
          baselineTokens: 0,
          actualTokens: 0,
          callsAvoided: 0,
          eventCount: 0,
          levers: new Map(),
        };
        bySurface.set(surface, row);
      }
      return row;
    };

    for (const raw of eventsRes.data ?? []) {
      const surface = TOKEN_SAVINGS_SURFACES.includes(raw.surface as TokenSavingsSurface)
        ? (raw.surface as TokenSavingsSurface)
        : "other";
      const agg = ensure(surface);
      const saved = toInt(raw.tokens_saved);
      agg.tokensSaved += saved;
      agg.baselineTokens += toInt(raw.baseline_tokens);
      agg.actualTokens += toInt(raw.actual_tokens);
      agg.callsAvoided += toInt(raw.calls_avoided);
      agg.eventCount += 1;
      const lever = raw.lever as TokenSavingsLever;
      const leverRow = agg.levers.get(lever) ?? { tokensSaved: 0, eventCount: 0 };
      leverRow.tokensSaved += saved;
      leverRow.eventCount += 1;
      agg.levers.set(lever, leverRow);
    }

    const rows: TokenSavingsSurfaceRow[] = TOKEN_SAVINGS_SURFACES.map((surface) => {
      const agg = bySurface.get(surface);
      const tokensUsed = usedBySurface.get(surface) ?? 0;
      const tokensSavedTotal = agg?.tokensSaved ?? 0;
      const baseline = agg?.baselineTokens ?? 0;
      const percent = baseline > 0 ? tokensSavedTotal / baseline : null;
      return {
        surface,
        tokensSaved: tokensSavedTotal,
        baselineTokens: baseline,
        actualTokens: agg?.actualTokens ?? 0,
        tokensUsed,
        callsAvoided: agg?.callsAvoided ?? 0,
        eventCount: agg?.eventCount ?? 0,
        percent,
        levers: [...(agg?.levers.entries() ?? [])]
          .map(([lever, value]) => ({ lever, ...value }))
          .sort((a, b) => b.tokensSaved - a.tokensSaved),
      };
    }).filter((row) => row.eventCount > 0 || row.tokensUsed > 0 || row.tokensSaved > 0);

    const chatSaved = rows.find((r) => r.surface === "chat")?.tokensSaved ?? 0;
    const mcpSaved = rows.find((r) => r.surface === "mcp")?.tokensSaved ?? 0;
    const restSaved = rows
      .filter((r) => r.surface !== "chat" && r.surface !== "mcp")
      .reduce((sum, r) => sum + r.tokensSaved, 0);

    return {
      range,
      totalSaved: rows.reduce((sum, r) => sum + r.tokensSaved, 0),
      totalUsed: rows.reduce((sum, r) => sum + r.tokensUsed, 0),
      callsAvoided: rows.reduce((sum, r) => sum + r.callsAvoided, 0),
      chatSaved,
      mcpSaved,
      restSaved,
      bySurface: rows,
    };
  },
};

export function emptyTokenSavingsSummary(range: TokenSavingsRange = "all"): TokenSavingsSummary {
  return {
    range,
    totalSaved: 0,
    totalUsed: 0,
    callsAvoided: 0,
    chatSaved: 0,
    mcpSaved: 0,
    restSaved: 0,
    bySurface: [],
  };
}
